import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { UserPlus, Trash2, Users, Mail, Check, AlertTriangle, Copy, Clock, Send, X as XIcon } from 'lucide-react'
import api from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAuthStore } from '@/stores/authStore'

interface Member {
  user_id: string
  email: string
  full_name: string | null
  role: string
  joined_at: string
  is_active: boolean
}

interface InviteFormData {
  email: string
  role: string
}

const ROLE_LABELS: Record<string, string> = {
  org_owner: 'Owner',
  org_admin: 'Admin',
  org_billing: 'Billing',
  org_member: 'Member',
  org_viewer: 'Viewer',
}

const ROLE_COLORS: Record<string, string> = {
  org_owner: 'bg-purple-100 text-purple-800',
  org_admin: 'bg-blue-100 text-blue-800',
  org_billing: 'bg-green-100 text-green-800',
  org_member: 'bg-gray-100 text-gray-800',
  org_viewer: 'bg-gray-100 text-gray-600',
}

interface InviteResult {
  added?: boolean
  invited?: boolean
  email?: string
  role?: string
  email_sent?: boolean
  message?: string
  accept_url?: string
  email_error?: string
}

export default function Members() {
  const queryClient = useQueryClient()
  const { activeOrgRole, user } = useAuthStore()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [banner, setBanner] = useState<{ kind: 'success' | 'warning' | 'error'; result: InviteResult } | null>(null)
  const [copiedLink, setCopiedLink] = useState(false)

  const { data: members = [], isLoading } = useQuery<Member[]>({
    queryKey: ['org-members'],
    queryFn: () => api.get('/orgs/current/members').then((r) => r.data),
  })

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<InviteFormData>({
    defaultValues: { email: '', role: 'org_member' },
  })

  const inviteMutation = useMutation({
    mutationFn: (data: InviteFormData) =>
      api.post<InviteResult>('/orgs/current/invitations', data).then((r) => r.data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['org-members'] })
      queryClient.invalidateQueries({ queryKey: ['org-invitations'] })
      setInviteOpen(false)
      reset()
      // Decide banner kind:
      // - added=true                                → success ("Now a member")
      // - invited=true & email_sent=true            → success ("Invitation emailed")
      // - invited=true & email_sent=false           → warning (copy link manually)
      let kind: 'success' | 'warning' | 'error' = 'success'
      if (result.invited && result.email_sent === false) kind = 'warning'
      setBanner({ kind, result })
      // Auto-clear success banners after 6s; keep warnings until dismissed
      if (kind === 'success') {
        setTimeout(() => setBanner((b) => (b?.kind === 'success' ? null : b)), 6000)
      }
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail || 'Failed to send invitation.'
      setBanner({
        kind: 'error',
        result: { message: detail },
      })
    },
  })

  const copyAcceptLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    } catch {
      // ignore
    }
  }

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/orgs/current/members/${userId}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-members'] })
    },
  })

  // ── Pending invitations ─────────────────────────────────────────────
  interface PendingInvitation {
    id: string
    email: string
    role: string
    token: string
    accept_url: string
    expires_at: string | null
    is_expired: boolean
    created_at: string | null
    invited_by_name: string | null
    invited_by_email: string | null
  }

  const canManageMembers = ['org_owner', 'org_admin'].includes(activeOrgRole || '')

  const { data: invitations = [] } = useQuery<PendingInvitation[]>({
    queryKey: ['org-invitations'],
    queryFn: () => api.get('/orgs/current/invitations').then((r) => r.data),
    enabled: canManageMembers,
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/current/invitations/${id}`).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-invitations'] }),
  })

  const resendMutation = useMutation({
    mutationFn: (id: string) =>
      api
        .post<{ email: string; email_sent: boolean; email_error: string | null; accept_url: string }>(
          `/orgs/current/invitations/${id}/resend`,
        )
        .then((r) => r.data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['org-invitations'] })
      const kind: 'success' | 'warning' = result.email_sent ? 'success' : 'warning'
      setBanner({
        kind,
        result: {
          email: result.email,
          invited: true,
          email_sent: result.email_sent,
          accept_url: result.accept_url,
          email_error: result.email_error || undefined,
        },
      })
      if (kind === 'success') {
        setTimeout(() => setBanner((b) => (b?.kind === 'success' ? null : b)), 6000)
      }
    },
  })

  const [copiedForId, setCopiedForId] = useState<string | null>(null)
  const copyInvitationLink = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedForId(id)
      setTimeout(() => setCopiedForId(null), 2000)
    } catch {
      // ignore
    }
  }

  return (
    <div className="p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
        </div>
        {canManageMembers && (
          <Button onClick={() => setInviteOpen(true)} className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Invite Member
          </Button>
        )}
      </div>

      {banner && (
        <div
          className={`mb-6 rounded-md border p-4 flex items-start gap-3 ${
            banner.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-900'
              : banner.kind === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-red-200 bg-red-50 text-red-900'
          }`}
        >
          {banner.kind === 'success' ? (
            <Check className="h-5 w-5 mt-0.5 shrink-0 text-green-600" />
          ) : banner.kind === 'warning' ? (
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
          ) : (
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-red-600" />
          )}
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-sm font-medium">
              {banner.result.added
                ? `${banner.result.email} has been added to the team.`
                : banner.result.invited && banner.result.email_sent
                  ? `Invitation sent to ${banner.result.email}.`
                  : banner.result.invited
                    ? `Invitation created, but the email could not be delivered. Share the link below directly with ${banner.result.email}.`
                    : (banner.result.message || 'Action complete.')}
            </p>
            {banner.kind === 'warning' && banner.result.accept_url && (
              <div className="flex items-center gap-2 bg-white border border-amber-200 rounded p-2">
                <code className="flex-1 text-xs text-amber-900 truncate" title={banner.result.accept_url}>
                  {banner.result.accept_url}
                </code>
                <button
                  onClick={() => copyAcceptLink(banner.result.accept_url!)}
                  className="text-xs px-2 py-1 rounded border border-amber-300 hover:bg-amber-100 flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" />
                  {copiedLink ? 'Copied!' : 'Copy link'}
                </button>
              </div>
            )}
            {banner.kind === 'warning' && banner.result.email_error && (
              <p className="text-xs text-amber-700 mt-1">Email server said: {banner.result.email_error.slice(0, 200)}</p>
            )}
          </div>
          <button
            onClick={() => setBanner(null)}
            className="text-current opacity-60 hover:opacity-100 shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {members.length} member{members.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {members.map((member) => (
                <div key={member.user_id} className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                      {(member.full_name || member.email)[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {member.full_name || member.email}
                        {member.user_id === user?.id && (
                          <span className="ml-2 text-xs text-gray-400">(you)</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {ROLE_LABELS[member.role] || member.role}
                    </span>
                    {canManageMembers && member.user_id !== user?.id && member.role !== 'org_owner' && (
                      <button
                        onClick={() => removeMutation.mutate(member.user_id)}
                        disabled={removeMutation.isPending}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove member"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending invitations */}
      {canManageMembers && invitations.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              Pending invitations ({invitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {invitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-3 px-6 py-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 shrink-0">
                      <Mail className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate" title={inv.email}>
                        {inv.email}
                      </p>
                      <p className="text-xs text-gray-500">
                        Invited{' '}
                        {inv.invited_by_name || inv.invited_by_email
                          ? `by ${inv.invited_by_name || inv.invited_by_email} `
                          : ''}
                        {inv.expires_at && !inv.is_expired && (
                          <>· expires {new Date(inv.expires_at).toLocaleDateString()}</>
                        )}
                        {inv.is_expired && <span className="text-red-600"> · expired</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        ROLE_COLORS[inv.role] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {ROLE_LABELS[inv.role] || inv.role}
                    </span>
                    {inv.is_expired ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                        Expired
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        Pending
                      </span>
                    )}
                    <button
                      onClick={() => copyInvitationLink(inv.id, inv.accept_url)}
                      className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Copy invitation link"
                    >
                      {copiedForId === inv.id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => resendMutation.mutate(inv.id)}
                      disabled={resendMutation.isPending}
                      className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Resend invitation email"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Revoke invitation for ${inv.email}?`)) {
                          revokeMutation.mutate(inv.id)
                        }
                      }}
                      disabled={revokeMutation.isPending}
                      className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Revoke invitation"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Invite Team Member
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={handleSubmit((data) => inviteMutation.mutate(data))}
            className="space-y-4 mt-2"
          >
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },
                })}
                placeholder="colleague@company.com"
                className="mt-1"
              />
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label>Role</Label>
              <Select
                value={watch('role')}
                onValueChange={(v) => setValue('role', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="org_admin">Admin — manage projects & users</SelectItem>
                  <SelectItem value="org_billing">Billing — billing access only</SelectItem>
                  <SelectItem value="org_member">Member — create & manage projects</SelectItem>
                  <SelectItem value="org_viewer">Viewer — read-only access</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                disabled={inviteMutation.isPending}
                className="flex-1"
              >
                {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setInviteOpen(false)}
              >
                Cancel
              </Button>
            </div>

            {inviteMutation.isError && (
              <p className="text-sm text-red-500 text-center">
                {(inviteMutation.error as any)?.response?.data?.detail || 'Failed to send invitation. Please try again.'}
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
